import { useQuery } from '@tanstack/react-query';
import { chatApi } from '../api/chat';
import { toCsv, downloadCsv } from '../lib/csv';
import type { ChatbotLead } from '../types';

type Props = {
  af: string | null;
  /** False while this franchise's chatbot hasn't been switched on in the
   *  dashboard — their website chat is still Digital AirStrike's. */
  chatOn: boolean;
};

const AIRSTRIKE_URL = 'https://dashboard.chatpath.com';

/** Stable, Excel-friendly timestamp (UTC-stored values shown as local). */
function csvDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z');
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function todayStamp(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** The store's enum, in the words a franchisee uses. */
const INQUIRY_LABEL: Record<string, string> = {
  home_care_lead: 'Looking for care',
  ltci_policy_review: 'LTCi policy review',
  caregiver_lead: 'Job enquiry',
  unknown: 'Not specified',
};

/** Extra context the form captured, shown as a short note rather than two more
 *  mostly-empty columns: LTCi asks claim vs review, home care asks who it is for. */
function detailFor(l: ChatbotLead): string {
  if (l.ltci_intent) return l.ltci_intent === 'claim' ? 'Making a claim' : 'Policy review';
  if (l.care_recipient) return l.care_recipient === 'self' ? 'For themselves' : 'For a loved one';
  return '';
}

function exportLeadsCsv(af: string, leads: ChatbotLead[]): void {
  const headers = ['Date', 'Name', 'Phone', 'Email', 'ZIP', 'Enquiry', 'Detail', 'Status'];
  const rows = leads.map((l) => [
    csvDate(l.started_at),
    l.visitor_name ?? '',
    l.visitor_phone ?? '',
    l.visitor_email ?? '',
    l.visitor_zip ?? '',
    INQUIRY_LABEL[l.inquiry_type] ?? l.inquiry_type,
    detailFor(l),
    l.status,
  ]);
  downloadCsv(`chatbot-leads_${af}_${todayStamp()}.csv`, toCsv(headers, rows));
}

export default function ChatbotLeadsSubTab({ af, chatOn }: Props) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['chatbot-leads', af],
    queryFn: () => chatApi.listLeads(af as string),
    enabled: Boolean(af) && chatOn,
    staleTime: 60 * 1000,
  });

  if (!af) {
    return (
      <div className="tab-placeholder">
        <h3>Chatbot Leads</h3>
        <p>Select a franchisee to view their chatbot leads.</p>
      </div>
    );
  }

  // Their website chat is still Digital AirStrike's, so their chat leads are
  // over there, not here. Say so plainly and hand them the login rather than
  // showing an empty table that reads like "nobody contacted you".
  if (!chatOn) {
    return (
      <div className="tab-placeholder" style={{ padding: '44px 24px' }}>
        <h3>Your chat leads are in Digital AirStrike</h3>
        <p style={{ maxWidth: 520, margin: '0 auto' }}>
          The chatbot on your website is still run by Digital AirStrike, so leads from it are
          collected there. Log in to your Digital AirStrike dashboard to see them.
        </p>
        <p style={{ marginTop: 20 }}>
          <a
            href={AIRSTRIKE_URL}
            target="_blank"
            rel="noreferrer"
            style={{
              display: 'inline-block',
              padding: '10px 20px',
              background: '#4338ca',
              color: '#fff',
              borderRadius: 6,
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            Open Digital AirStrike →
          </a>
        </p>
        <p style={{ marginTop: 16, fontSize: 13, maxWidth: 520, marginLeft: 'auto', marginRight: 'auto' }}>
          When your chatbot moves over to the dashboard, your leads will start appearing on this
          tab automatically and you won't need to log in there any more.
        </p>
      </div>
    );
  }
  if (isLoading) {
    return (
      <div className="tab-placeholder">
        <h3>Loading chatbot leads…</h3>
      </div>
    );
  }
  if (error) {
    return (
      <div className="tab-placeholder">
        <h3>Chatbot Leads Unavailable</h3>
        <p>Could not load chatbot leads: {(error as Error).message}</p>
      </div>
    );
  }

  const leads = data?.leads ?? [];

  // The store being off is a different thing from having no leads yet, and
  // saying "no leads" when we simply cannot see them would be a lie.
  if (data && data.storeEnabled === false) {
    return (
      <div className="tab-placeholder">
        <h3>Chatbot Leads</h3>
        <p>The conversation store isn’t connected in this environment, so no leads are recorded here.</p>
      </div>
    );
  }

  if (leads.length === 0) {
    return (
      <div className="tab-placeholder">
        <h3>No chatbot leads yet</h3>
        <p>
          When someone leaves their details in the chat on this location’s website, they’ll appear
          here — and you can download the list as a spreadsheet.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="webform-leads-header">
        <span className="webform-leads-count">
          {leads.length} lead{leads.length === 1 ? '' : 's'} from the website chat
        </span>
        <button
          type="button"
          className="export-csv-btn"
          onClick={() => exportLeadsCsv(af, leads)}
        >
          ⬇ Download CSV
        </button>
      </div>

      <div className="calls-table">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Name</th>
              <th>Phone</th>
              <th>Email</th>
              <th>ZIP</th>
              <th>Enquiry</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => {
              const detail = detailFor(l);
              return (
                <tr key={l.id}>
                  <td>{csvDate(l.started_at)}</td>
                  <td>{l.visitor_name || '—'}</td>
                  <td>
                    {l.visitor_phone
                      ? <a href={`tel:${l.visitor_phone}`}>{l.visitor_phone}</a>
                      : '—'}
                  </td>
                  <td>
                    {l.visitor_email
                      ? <a href={`mailto:${l.visitor_email}`}>{l.visitor_email}</a>
                      : '—'}
                  </td>
                  {/* Job enquiries are never asked for a ZIP — an em dash here
                      means "not collected", not "they refused". */}
                  <td>{l.visitor_zip || '—'}</td>
                  <td>
                    {INQUIRY_LABEL[l.inquiry_type] ?? l.inquiry_type}
                    {detail && <span className="webform-leads-synced"> · {detail}</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
